import { PROPERTY_GRID_SUBJECT_PROPERTY, alertMatchesFocusIdentity } from '@aloft/toolkit-shared';

import { RefObject, useContext, useEffect, useMemo } from 'react';

import { ALERT_HOVER_PREVIEW_CELL_ATTR } from '../../../../hooks/useToaster/constants.ts';
import { getActiveAlertForPropertyGridCell } from '../../../../modules/alerts/getActiveAlertForPropertyGridCell.ts';
import {
  shouldPreserveAlertHoverPreview,
  syncAlertHoverPreview,
} from '../../../../modules/alerts/syncAlertHoverPreview.ts';
import { useAlerts } from '../../../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectReferenceBarUI } from '../../../../redux/features/selectors/referenceBarUISelectors.ts';
import {
  selectToastSuppressedAlertTypes,
  selectTriggeredAlerts,
} from '../../../../redux/features/selectors/uiSelectors.ts';
import { setFocusedPropertyGridCellKey } from '../../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { PropertyGridFocusContext } from '../../PropertyGridFocusContext.ts';
import { PropertyGridCellKey, PropertyGridCellKeyId } from '../../types.ts';
import { resolveCellKeyId } from '../utils.tsx';
import { ALERT_TARGET_FIELD_TITLE } from '../styles.ts';

type UsePropertyGridCellFocusParams = {
  cellKeyId: PropertyGridCellKeyId;
  propertyId: string;
  fieldRef: RefObject<HTMLElement>;
  isFieldComplete?: boolean;
  isArv?: boolean;
};

type UsePropertyGridCellFocusResult = {
  isAlertTarget: boolean;
  isAlertHighlighted: boolean;
  isAlertStyled: boolean;
  alertStyleVariant: 'error' | 'warning';
  alertCellTooltip?: string;
  isAlertToastVisible: boolean;
  clearAlertTarget: () => void;
};

export const usePropertyGridCellFocus = ({
  cellKeyId,
  propertyId,
  fieldRef,
  isFieldComplete = false,
  isArv = false,
}: UsePropertyGridCellFocusParams): UsePropertyGridCellFocusResult => {
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;
  const resolvedCellKeyId = resolveCellKeyId(cellKeyId, isSubject, isArv);
  const cellKey: PropertyGridCellKey = isSubject
    ? `subject-${resolvedCellKeyId}`
    : `${propertyId}-${resolvedCellKeyId}`;

  const { activeTargetKey, registerField, unregisterField } = useContext(PropertyGridFocusContext);
  const {
    focusedAlertType,
    focusedAlertPropertyID,
    focusedAlertTargetCellKey,
    hoveredAlertType,
    hoveredAlertPropertyID,
    hoveredAlertTargetCellKey,
  } = useAppSelector(selectReferenceBarUI);
  const triggeredAlerts = useAppSelector(selectTriggeredAlerts);
  const toastSuppressedAlertTypes = useAppSelector(selectToastSuppressedAlertTypes);
  const alerts = useAlerts();
  const isAlertTarget = activeTargetKey === cellKey;
  const targetingAlert = useMemo(
    () => getActiveAlertForPropertyGridCell(alerts, cellKey),
    [alerts, cellKey],
  );
  const isAlertHighlighted =
    isAlertTarget ||
    alertMatchesFocusIdentity(
      targetingAlert,
      hoveredAlertType
        ? {
            type: hoveredAlertType,
            propertyID: hoveredAlertPropertyID,
            targetCellKey: hoveredAlertTargetCellKey,
          }
        : null,
    ) ||
    alertMatchesFocusIdentity(
      targetingAlert,
      focusedAlertType
        ? {
            type: focusedAlertType,
            propertyID: focusedAlertPropertyID,
            targetCellKey: focusedAlertTargetCellKey,
          }
        : null,
    );
  const isTriggeredAlertCell = Boolean(
    targetingAlert && triggeredAlerts.includes(targetingAlert.type),
  );
  const isAlertStyled = Boolean(
    targetingAlert && (isAlertHighlighted || isTriggeredAlertCell) && !isFieldComplete,
  );
  const alertStyleVariant = targetingAlert?.severity ?? 'error';
  const isAlertToastVisible = Boolean(
    targetingAlert &&
      triggeredAlerts.includes(targetingAlert.type) &&
      !toastSuppressedAlertTypes.includes(targetingAlert.type),
  );
  const alertCellTooltip =
    targetingAlert?.fixingInfo.targetCellTooltip ??
    (isAlertStyled ? ALERT_TARGET_FIELD_TITLE : undefined);
  const dispatch = useAppDispatch();

  useEffect(() => {
    registerField(cellKey, fieldRef);
    return () => unregisterField(cellKey);
  }, [cellKey]);

  useEffect(() => {
    if (!targetingAlert && isAlertTarget) {
      dispatch(setFocusedPropertyGridCellKey(undefined));
    }
  }, [dispatch, isAlertTarget, targetingAlert]);

  useEffect(() => {
    if (!targetingAlert) {
      return undefined;
    }

    let element: HTMLElement | null = null;
    let removeListeners: (() => void) | undefined;

    const setup = () => {
      element = fieldRef.current;
      if (!element) {
        return;
      }

      element.setAttribute(ALERT_HOVER_PREVIEW_CELL_ATTR, '');

      const handleMouseEnter = () => {
        syncAlertHoverPreview(dispatch, targetingAlert, { scrollTargetIntoView: false });
      };

      const handleMouseLeave = (event: MouseEvent) => {
        if (element?.contains(event.relatedTarget as Node)) {
          return;
        }
        if (shouldPreserveAlertHoverPreview(event.relatedTarget as HTMLElement | null)) {
          return;
        }
        syncAlertHoverPreview(dispatch, null);
      };

      element.addEventListener('mouseenter', handleMouseEnter);
      element.addEventListener('mouseleave', handleMouseLeave);

      removeListeners = () => {
        element?.removeAttribute(ALERT_HOVER_PREVIEW_CELL_ATTR);
        element?.removeEventListener('mouseenter', handleMouseEnter);
        element?.removeEventListener('mouseleave', handleMouseLeave);
      };
    };

    const raf = requestAnimationFrame(setup);

    return () => {
      cancelAnimationFrame(raf);
      removeListeners?.();
    };
  }, [cellKey, dispatch, targetingAlert]);

  const clearAlertTarget = () => {
    if (isAlertTarget) dispatch(setFocusedPropertyGridCellKey(undefined));
  };

  return {
    isAlertTarget,
    isAlertHighlighted,
    isAlertStyled,
    alertStyleVariant,
    alertCellTooltip,
    isAlertToastVisible,
    clearAlertTarget,
  };
};
